import * as Ord from 'fp-ts/Ord';
import * as R from 'fp-ts/Record';
import { pipe } from 'fp-ts/function';

import { isNotEmpty } from '@utils/guards';

export type Route = {
    url: string;
    component: string;
};

/** @type {Record<string, Routes>} */
export const Routes = {
    Home: {
        url: '/',
        component: 'Home/Home',
    },
    Contacts: {
        url: '/contacts',
        component: 'ContactsView/ContactsView',
    },
    Static: {
        url: '/:slug',
        component: 'StaticView/StaticView',
    },
    News: {
        url: '/news?type=news',
        component: 'News/News',
    },
    Promotions: {
        url: '/news?type=promotion',
        component: 'News/News',
    },
    AllArticles: {
        url: '/news',
        component: 'News/News',
    },
    Category: {
        url: '/catalog/:slug',
        component: 'CategoryView/CategoryView',
    },
    Article: {
        url: '/news/:slug',
        component: 'Article/Article',
    },
    Catalog: {
        url: '/catalog',
        component: 'CatalogView/CatalogView',
    },
    Search: {
        url: '/search',
        component: 'CategoryView/CategoryView',
    },
    Cart: {
        url: '/cart',
        component: 'CartView/CartView',
    },
    Checkout: {
        url: '/checkout',
        component: 'Checkout/Checkout',
    },
    InfoPage: {
        url: '/:slug',
        component: 'InfoPage/InfoPage',
    },
    Product: {
        url: '/catalog/:path',
        component: 'ProductView/ProductView',
    },
    Recipes: {
        url: '/recipes',
        component: 'Recipes/Recipes',
    },
    RecipesRubric: {
        url: '/recipes?rubric_slug=:rubric_slug',
        component: 'Recipes/Recipes',
    },
    Recipe: {
        url: '/recipes/:slug',
        component: 'Recipes/Recipes',
    },
    ForBusiness: {
        url: '/for-business',
        component: 'ForBusiness/ForBusiness',
    },
    BusinessFormSuccess: {
        url: '/for-business/success',
        component: 'BusinessFormSuccess/BusinessFormSuccess',
    },
    Profile: {
        url: '/profile',
        component: 'ProfileView/ProfileView',
    },
    FavoriteRecipes: {
        url: '/profile/favorite-recipes',
        component: 'FavoriteRecipesView/FavoriteRecipesView',
    },
    FavoriteRecipesRubric: {
        url: '/profile/favorite-recipes?rubric_slug=:rubric_slug',
        component: 'FavoriteRecipesView/FavoriteRecipesView',
    },
    FavoriteProducts: {
        url: '/profile/favorite-products',
        component: 'FavoriteProductsView/FavoriteProductsView',
    },
    OrderHistory: {
        url: '/profile/orders',
        component: 'OrdersView/OrdersView',
    },
    OrderSuccess: {
        url: '/payment/success/:order_number',
        component: 'OrderReceived/OrderSuccess',
    },
    OrderFailure: {
        url: '/payment/failure/:order_number',
        component: 'OrderReceived/OrderFailure',
    },
};
export type KnownRoutes = keyof typeof Routes;

export const makeRoute = (
    route: KnownRoutes,
    params: Record<string, string | number> = {},
): string => {
    const stringRoute = Routes[route].url.toString();
    return pipe(
        params,
        R.reduceWithIndex(Ord.fromCompare(() => 1))(stringRoute, (queryParam, result, value) =>
            result.replace(new RegExp(`:${queryParam}`, 'g'), value.toString()),
        ),
    );
};
export const makeHomeRoute = () => makeRoute('Home');
export const makeContactsRoute = () => makeRoute('Contacts');
export const makeNewsArticlesRoute = () => makeRoute('News');
export const makeNewsPromotionsRoute = () => makeRoute('Promotions');
export const makeAllArticlesRoute = () => makeRoute('AllArticles');
export const makeNewsDetailsRoute = (slug: string) => makeRoute('Article', { slug });
export const makeForBusinessRoute = () => makeRoute('ForBusiness');
export const makeBusinessFormSuccessRoute = () => makeRoute('BusinessFormSuccess');
export const makeCatalogRoute = () => makeRoute('Catalog');
export const makeSearchRoute = () => makeRoute('Search');
export const makeCategoryRoute = (slug: string) => makeRoute('Category', { slug });
export const makePromoCategoryRoute = (slug: string) =>
    makeRoute('Category', { slug: `${slug}?is_promo=true` });
export const makeNewProductsCategoryRoute = (slug: string) =>
    makeRoute('Category', { slug: `${slug}?is_novelty=true` });
export const makeProductRoute = (slug: string, categorySlug: string) =>
    makeRoute('Product', { path: `${categorySlug}/${slug}` });
export const makeInfoPageRoute = (slug: string) => makeRoute('InfoPage', { slug });
export const makeRecipesRoute = () => makeRoute('Recipes');
export const makeRecipesRubricRoute = (rubric_slug: string) =>
    makeRoute('RecipesRubric', { rubric_slug });
export const makeRecipeRoute = (slug: string) => makeRoute('Recipe', { slug });
export const makeCartRoute = () => makeRoute('Cart');
export const makeCheckoutRoute = () => makeRoute('Checkout');
export const makeProfileRoute = () => makeRoute('Profile');
export const makeFavoriteRecipesRoute = () => makeRoute('FavoriteRecipes');
export const makeFavoriteRecipesRubricRoute = (rubric_slug: string) =>
    makeRoute('FavoriteRecipesRubric', { rubric_slug });
export const makeFavoriteProductsRoute = () => makeRoute('FavoriteProducts');
export const makeOrderHistoryRoute = () => makeRoute('OrderHistory');
export const makeOrderSuccessRoute = (order_number: string) =>
    makeRoute('OrderSuccess', { order_number });
export const makeOrderFailureRoute = (order_number: string) =>
    makeRoute('OrderFailure', { order_number });

export const getLink = (route: KnownRoutes | string) =>
    R.has(route, Routes) ? makeRoute(route) : route;

// --- Pagination url generator ---
export type GeneratePageUrlProps = {
    currentQuery?: Record<string, string | string[] | undefined>;
    currentPath?: string;
};

export const paginationDefaultOptions = {
    isFirstAndLastAlwaysVisible: true,
    extremePagesLimit: 1,
    nearbyPagesLimit: 1,
};

export const generatePageUrl =
    (props: GeneratePageUrlProps) =>
    (page: number): string => {
        const pageQuery = page !== 1 ? { page: page.toString() } : {};

        if (props.currentQuery !== undefined) {
            const { page: _currentPage, ...currentQuery } = props.currentQuery;
            const newQuery = { ...currentQuery, ...pageQuery };

            if (Object.entries(newQuery).length > 0) {
                const params = Object.entries({ ...currentQuery, ...pageQuery })
                    .filter(
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        ([_key, value]) => typeof value === 'string' || Array.isArray(value),
                    )
                    .map(([key, value]) => {
                        if (typeof value === 'string') {
                            return `${key}=${value}`;
                        }
                        if (Array.isArray(value)) {
                            return value.map((arrayValue) => `${key}=${arrayValue}`).join('&');
                        }
                    })
                    .join('&');
                return `${props.currentPath}${isNotEmpty(params) ? `?${params}` : ''}`;
            } else {
                return props.currentPath ?? '';
            }
        } else {
            if (page !== 1) {
                return `${props.currentPath}?page=${page.toString()}`;
            } else {
                return props.currentPath ?? '';
            }
        }
    };
